import { useEffect, useRef } from 'react';
import './WildChargeAnimation.css';

export default function WildChargeAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container = defenderEl.closest(".battle-container");
    
    if (!attackerEl || !defenderEl || !container) {
      onComplete();
      return;
    }

    // Get positions for both Pokemon
    const attackerRect = attackerEl.getBoundingClientRect();
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate centers
    const attackerCenterX = attackerRect.left - containerRect.left + attackerRect.width / 2;
    const attackerCenterY = attackerRect.top - containerRect.top + attackerRect.height / 2;
    const defenderCenterX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const defenderCenterY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    // Calculate direction for charge
    const dx = defenderCenterX - attackerCenterX;
    const dy = defenderCenterY - attackerCenterY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);

    // Track all created elements for cleanup
    const elements = [];
    const timers = [];
    
    // PHASE 1: ELECTRIC CHARGE BUILDUP (0-400ms)
    // Initial attacker animation - electric charge preparation
    attackerEl.style.transition = 'transform 0.4s ease-in-out, filter 0.4s ease-in-out';
    attackerEl.style.transform = 'scale(1.1)';
    attackerEl.style.filter = 'brightness(1.4) drop-shadow(0 0 20px #FFD700)';
    
    // Create electric aura around attacker
    const electricAura = document.createElement('div');
    electricAura.className = 'wild-charge-electric-aura';
    electricAura.style.left = `${attackerCenterX}px`;
    electricAura.style.top = `${attackerCenterY}px`;
    container.appendChild(electricAura);
    elements.push(electricAura);
    
    // Create electric charge sparks around attacker
    for (let i = 0; i < 16; i++) {
      const sparkTimer = setTimeout(() => {
        const spark = document.createElement('div');
        spark.className = 'wild-charge-charge-spark';
        
        const sparkAngle = (i / 16) * Math.PI * 2;
        const sparkDistance = 35 + Math.random() * 20;
        spark.style.left = `${attackerCenterX + Math.cos(sparkAngle) * sparkDistance}px`;
        spark.style.top = `${attackerCenterY + Math.sin(sparkAngle) * sparkDistance}px`;
        spark.style.setProperty('--spark-delay', `${i * 0.025}s`);
        
        container.appendChild(spark);
        elements.push(spark);
        
        // Auto-remove spark
        const sparkRemoveTimer = setTimeout(() => {
          if (spark.isConnected) {
            spark.remove();
          }
        }, 600);
        timers.push(sparkRemoveTimer);
      }, i * 25);
      timers.push(sparkTimer);
    }

    // PHASE 2: WILD CHARGE DASH (400-800ms)
    const chargeTimer = setTimeout(() => {
      // Create electric charge trail during movement
      const chargeTrail = document.createElement('div');
      chargeTrail.className = 'wild-charge-trail';
      chargeTrail.style.left = `${attackerCenterX}px`;
      chargeTrail.style.top = `${attackerCenterY}px`;
      chargeTrail.style.setProperty('--trail-angle', `${angle}rad`);
      chargeTrail.style.setProperty('--trail-length', `${distance * 0.8}px`);
      
      container.appendChild(chargeTrail);
      elements.push(chargeTrail);
      
      // Move attacker toward opponent with electric effects
      attackerEl.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      attackerEl.style.transform = `translate(${dx * 0.7}px, ${dy * 0.7}px) scale(1.05)`;
      attackerEl.style.filter = 'brightness(1.6) drop-shadow(0 0 25px #FFD700)';
      
      // Create electric sparks during charge movement
      for (let i = 0; i < 12; i++) {
        const movementSparkTimer = setTimeout(() => {
          const movementSpark = document.createElement('div');
          movementSpark.className = 'wild-charge-movement-spark';
          
          // Position sparks along the charge path
          const progress = i / 12;
          const sparkX = attackerCenterX + (dx * progress * 0.7);
          const sparkY = attackerCenterY + (dy * progress * 0.7);
          
          movementSpark.style.left = `${sparkX + (Math.random() - 0.5) * 30}px`;
          movementSpark.style.top = `${sparkY + (Math.random() - 0.5) * 30}px`;
          movementSpark.style.setProperty('--movement-delay', `${i * 0.03}s`);
          
          container.appendChild(movementSpark);
          elements.push(movementSpark);
          
          const movementSparkRemoveTimer = setTimeout(() => {
            if (movementSpark.parentNode) {
              movementSpark.remove();
            }
          }, 800);
          timers.push(movementSparkRemoveTimer);
        }, i * 30);
        timers.push(movementSparkTimer);
      }
      
    }, 400);
    timers.push(chargeTimer);
        
    // PHASE 3: ELECTRIC IMPACT (800-1200ms)
    const impactTimer = setTimeout(() => {
      // Make defender react with electric impact
      defenderEl.classList.add('wild-charge-impact');
      defenderEl.style.filter = 'brightness(1.8) hue-rotate(60deg) saturate(1.8)';
      defenderEl.style.transform = 'translateX(15px) scale(0.9)';
      
      // Create electric impact explosion
      const impact = document.createElement('div');
      impact.className = 'wild-charge-impact-explosion';
      impact.style.left = `${defenderCenterX}px`;
      impact.style.top = `${defenderCenterY}px`;
      
      container.appendChild(impact);
      elements.push(impact);
      
      // Create electric discharge bolts around impact
      for (let i = 0; i < 16; i++) {
        const boltTimer = setTimeout(() => {
          const bolt = document.createElement('div');
          bolt.className = 'wild-charge-discharge-bolt';
          
          const boltAngle = Math.random() * Math.PI * 2;
          const boltDistance = 20 + Math.random() * 50;
          bolt.style.left = `${defenderCenterX + Math.cos(boltAngle) * boltDistance}px`;
          bolt.style.top = `${defenderCenterY + Math.sin(boltAngle) * boltDistance}px`;
          
          bolt.style.setProperty('--bolt-length', `${15 + Math.random() * 25}px`);
          bolt.style.setProperty('--bolt-angle', `${boltAngle}rad`);
          bolt.style.setProperty('--bolt-delay', `${i * 0.02}s`);
          
          container.appendChild(bolt);
          elements.push(bolt);
          
          const boltRemoveTimer = setTimeout(() => {
            if (bolt.parentNode) {
              bolt.remove();
            }
          }, 600);
          timers.push(boltRemoveTimer);
        }, i * 25);
        timers.push(boltTimer);
      }
      
      // Reset defender after impact
      const defenderResetTimer = setTimeout(() => {
        defenderEl.classList.remove('wild-charge-impact');
        defenderEl.style.filter = '';
        defenderEl.style.transform = '';
      }, 400);
      timers.push(defenderResetTimer);
      
    }, 800);
    timers.push(impactTimer);
    
    // PHASE 4: RECOIL EFFECT (1200-1600ms)
    const recoilTimer = setTimeout(() => {
      // Move attacker back to original position with recoil effect
      attackerEl.style.transition = 'transform 0.4s ease-out, filter 0.4s ease-out';
      attackerEl.style.transform = 'scale(0.95)';
      attackerEl.style.filter = 'brightness(0.8) saturate(0.7)';
      
      // Create recoil sparks around attacker
      for (let i = 0; i < 8; i++) {
        const recoilSparkTimer = setTimeout(() => {
          const recoilSpark = document.createElement('div');
          recoilSpark.className = 'wild-charge-recoil-spark';
          
          const recoilAngle = Math.random() * Math.PI * 2;
          const recoilDistance = 20 + Math.random() * 30;
          recoilSpark.style.left = `${attackerCenterX + Math.cos(recoilAngle) * recoilDistance}px`;
          recoilSpark.style.top = `${attackerCenterY + Math.sin(recoilAngle) * recoilDistance}px`;
          recoilSpark.style.setProperty('--recoil-delay', `${i * 0.05}s`);
          
          container.appendChild(recoilSpark);
          elements.push(recoilSpark);
          
          const recoilSparkRemoveTimer = setTimeout(() => {
            if (recoilSpark.parentNode) {
              recoilSpark.remove();
            }
          }, 800);
          timers.push(recoilSparkRemoveTimer);
        }, i * 50);
        timers.push(recoilSparkTimer);
      }
      
    }, 1200);
    timers.push(recoilTimer);
    
    // PHASE 5: RESET ATTACKER (1600-2000ms)
    const resetTimer = setTimeout(() => {
      // Reset attacker to normal state
      attackerEl.style.transition = 'transform 0.4s ease-out, filter 0.4s ease-out';
      attackerEl.style.transform = '';
      attackerEl.style.filter = '';
    }, 1600);
    timers.push(resetTimer);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 2000);
    timers.push(completeTimer);
    
    // Cleanup function
    return () => {
      // Clear all timers
      timers.forEach(timer => clearTimeout(timer));
      
      // Reset attacker
      if (attackerEl) {
        attackerEl.style.transition = '';
        attackerEl.style.transform = '';
        attackerEl.style.filter = '';
      }
      
      // Reset defender
      if (defenderEl) {
        defenderEl.classList.remove('wild-charge-impact');
        defenderEl.style.filter = '';
        defenderEl.style.transform = '';
      }
      
      // Remove all created elements
      elements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      
      // Remove any remaining effects
      const remainingElements = container.querySelectorAll(
        '.wild-charge-electric-aura, .wild-charge-charge-spark, .wild-charge-trail, ' +
        '.wild-charge-movement-spark, .wild-charge-impact-explosion, ' +
        '.wild-charge-discharge-bolt, .wild-charge-recoil-spark'
      );
      remainingElements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);
  
  return null;
}

WildChargeAnimation.moveType = "electric";
